<script lang="ts" setup>import { computed as _computed } from 'vue';

import {
  faCalendar, faEnvelope, faMailboxFlagUp,
  faMessageLines,
  faPrint,
} from '@fortawesome/pro-light-svg-icons'
import { formatDate, formatDeliveryDate, isBeforeNow } from '@consumer/utils/date'
import type { MailingAddress, PurchasedOrderGift } from '@consumer/types/serializers'
import { formatMailingAddress } from '@consumer/utils/address'
import { formatPhoneNumber } from '@consumer/utils/phoneNumber'
import { speedbumpText } from '@consumer/constants/disclaimers'

const emit = defineEmits<{
  giftNotDelivered: []
}>()

withDefaults(defineProps<{
  orderGift: PurchasedOrderGift
  wrapDateLine?: boolean
}>(), {  })

// const giftDeliveries = $computed(() => orderGift.giftDeliveries)

// Only most recent for each delivery method
const giftDeliveries = _computed(() => [
  __props.orderGift.giftDeliveries.filter(giftDelivery => giftDelivery.deliveryMethod === 'email')[0],
  __props.orderGift.giftDeliveries.filter(giftDelivery => giftDelivery.deliveryMethod === 'text')[0],
  __props.orderGift.giftDeliveries.filter(giftDelivery => giftDelivery.deliveryMethod === 'mail')[0],
  __props.orderGift.giftDeliveries.filter(giftDelivery => giftDelivery.deliveryMethod === 'print')[0],
].filter(Boolean).sort((a, b) => {
  if (a.deliveryScheduledAt && b.deliveryScheduledAt) {
    return new Date(a.deliveryScheduledAt).getTime() - new Date(b.deliveryScheduledAt).getTime()
  }
  return 0
}))
const mailTrackingUrl = _computed(() => __props.orderGift.mailTrackingUrl)

function openMailTrackingUrl () {
  if (!mailTrackingUrl.value) return
  // eslint-disable-next-line no-alert
  if (confirm(speedbumpText)) {
    window.open(mailTrackingUrl.value, '_blank', 'noopener')
  }
}
</script>

<template>
  <div
    v-for="giftDelivery in giftDeliveries"
    :key="giftDelivery.uuid"
    class="flex flex-col gap-2"
    :class="wrapDateLine ? 'lg:flex-col' : 'lg:flex-row lg:items-center'"
  >
    <div
      data-testid="order-gift-delivery-method"
      class="flex gap-2 items-center"
    >
      <template v-if="giftDelivery.deliveryMethod=== 'email'">
        <V2Icon :icon="faEnvelope" title="Email" class="stroke-v2-secondary"/>
        <div v-if="giftDelivery.deliveryAddressMaskedForDisplay">Verified email address</div>
        <div v-else-if="giftDelivery.recipientAddressSource === 'contact'">Saved email address</div>
        <div v-else>{{ giftDelivery.deliveryAddress }}</div>
      </template>
      <template v-else-if="giftDelivery.deliveryMethod === 'text'">
        <V2Icon :icon="faMessageLines" title="Text" class="stroke-v2-secondary"/>
        <div v-if="giftDelivery.deliveryAddressMaskedForDisplay">Verified phone number</div>
        <div v-else-if="giftDelivery.recipientAddressSource === 'contact'">Saved email address</div>
        <div v-else>{{ formatPhoneNumber(giftDelivery.deliveryAddress as string) }}</div>
      </template>
      <template v-else-if="giftDelivery.deliveryMethod === 'mail'">
        <V2Icon :icon="faMailboxFlagUp" title="Mail" class="stroke-v2-secondary"/>
        <div v-if="giftDelivery.deliveryAddressMaskedForDisplay">Verified mailing address</div>
        <div v-else-if="giftDelivery.recipientAddressSource === 'contact'">Saved mailing address</div>
        <div v-else>
          {{ formatMailingAddress(giftDelivery.deliveryAddress as MailingAddress, false) }}
        </div>
      </template>
      <template v-else>
        <V2Icon :icon="faPrint" title="Print" class="stroke-v2-secondary"/>
        <div>
          Printable PDF
        </div>
      </template>
    </div>

    <div :class="['hidden', wrapDateLine ? 'lg:hidden': 'lg:inline-block']">
      &bull;
    </div>

    <div data-testid="order-gift-delivery-date" class="flex gap-2 items-center">
      <V2Icon
        :icon="faCalendar"
        title="Calendar"
        class="stroke-v2-secondary"
        :class="wrapDateLine ? 'lg:inline-block' : 'lg:hidden'"
      />
      <div>
        <template v-if="orderGift.cancelledAt">
          Cancelled on <span class="no-screenshot">{{ formatDate(orderGift.cancelledAt) }}</span>
        </template>
        <template v-else-if="giftDelivery.deliveryMethod === 'mail'">
          <template v-if="!giftDelivery.deliveryCompletedAt || orderGift.statusLabel === 'Shipped'">
            Estimated delivery by <span class="no-screenshot">{{ formatDate(giftDelivery.deliveryScheduledAt) }}</span>
          </template>
          <template v-else>
            Sent to printer on <span class="no-screenshot">{{ formatDate(giftDelivery.deliveryCompletedAt) }}</span>
          </template>
        </template>
        <template v-else-if="giftDelivery.deliveryCompletedAt">
          Sent on <span class="no-screenshot">{{ formatDate(giftDelivery.deliveryCompletedAt) }}</span>
        </template>
        <template v-else-if="isBeforeNow(orderGift.delivery.deliverAt)">
          Scheduled for delivery ASAP
        </template>
        <template v-else>
          Scheduled for delivery <span class="no-screenshot">{{ formatDeliveryDate(orderGift.delivery) }}</span>
        </template>
      </div>
    </div>

    <div
      v-if="giftDelivery.deliveryMethod === 'mail' && mailTrackingUrl"
      class="text-v2-primary text-sm ml-6 lg:ml-0 mb-2 lg:mb-0"
    >
      <GButton
        flat
        name="track-mail-multi"
        label="Track"
        size="inherit"
        class="underline"
        @click.prevent="openMailTrackingUrl"
      />
    </div>

    <div
      v-if="giftDelivery.deliveryMethod === 'mail' && orderGift.canRedeliverMailable"
      class="text-v2-primary text-sm ml-6 lg:ml-0 mb-2 lg:mb-0"
    >
      <GButton
        flat
        name="gift-not-delivered"
        label="Gift didn't arrive?"
        size="inherit"
        class="underline"
        @click.prevent="emit('giftNotDelivered')"
      />
    </div>

    <div
      v-if="giftDelivery.deliveryMethod === 'mail' && orderGift.hasReplacementMailable"
      class="text-v2-secondary text-sm ml-6 lg:ml-0 mb-2 lg:mb-0"
    >
      (replacement)
    </div>
  </div>
</template>
